import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Activity, ShiftState } from '@wilson/interfaces';
import {
  DeleteDialogeHelperService,
  DeletePopUpParams,
} from '@wilson/shared-components';
import { NzModalService } from 'ng-zorro-antd/modal';
import { firstValueFrom, map } from 'rxjs';
import { requiresHeavyShiftDeleteConfirmation } from './requires-heavy-shift-delete-confirmation.fn';

export interface ShiftDeleteConfirmationInput {
  name: string;
  state?: ShiftState;
  activities?: Activity[] | null;
}

@Injectable({
  providedIn: 'root',
})
export class ShiftDeleteConfirmationService {
  private readonly nzModalService = inject(NzModalService);
  private readonly translate = inject(TranslateService);
  private readonly deleteDialogeHelperService = inject(
    DeleteDialogeHelperService,
  );

  async confirmShiftDelete(
    input: ShiftDeleteConfirmationInput,
  ): Promise<boolean> {
    if (requiresHeavyShiftDeleteConfirmation(input.state, input.activities)) {
      return this.confirmHeavyShiftDelete(input.name);
    }

    return this.confirmSimpleShiftDelete();
  }

  private confirmSimpleShiftDelete(): Promise<boolean> {
    const modalRef = this.nzModalService.confirm({
      nzTitle: this.translate.instant('page.shift.delete_confirm.title'),
      nzContent: this.translate.instant('page.shift.delete_confirm.message'),
      nzCancelText: this.translate.instant('general.cancel'),
      nzOkText: this.translate.instant('page.shift.delete_confirm.confirm'),
      nzOkType: 'primary',
      nzOkDanger: true,
      nzCentered: true,
      nzOnOk: () => {
        modalRef.close(true);
      },
    });

    return firstValueFrom(modalRef.afterClose.pipe(map((result) => !!result)));
  }

  private confirmHeavyShiftDelete(shiftName: string): Promise<boolean> {
    const deleteParams: DeletePopUpParams = {
      title: this.translate.instant('page.shifts.delete'),
      message: `${this.translate.instant(
        'modalDelete.confirmText.typeDeleteKeyword',
      )} <b>${this.translate.instant('modalDelete.deleteKeyword')}</b>`,
      objectToDeleteName: this.translate.instant('modalDelete.deleteKeyword'),
      twoStep: true,
      options: {
        customTexts: true,
        alertMessage: this.translate.instant('page.shift.delete_by_admin'),
      },
    };

    return firstValueFrom(
      this.deleteDialogeHelperService.openDeletePopUp(deleteParams),
    );
  }
}
